/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { findMpResults, saveEquipment } from 'apis/mp/MpResult';
import { MpResult } from 'models/mp/MpResult';
import CustomGrid from 'components/grids/CustomGrid';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { GatingContListPopUp } from '../../gtng/popup/GatingContListPopUp';
import { CustomEquipment } from 'models/common/popup/EquipmentMaster';
import { MpPlntList } from 'models/mp/MpPlnt';
import { useMessageBar } from 'hooks/useMessageBar';
import { IconButton } from 'components/buttons/IconSVG';
import { MpEquipmentHistoryPopUp } from '../popup/MpEquipmentHistoryPopUp';
import { open4mcc } from 'utils/ApiUtil';

interface Props {
  mpItemId?: string;
  mpPrdnPlntId?: string; // 생산플랜트ID
  copCd?: string;
  prdnPlntCd?: string;
  prdnPlntNm?: string;
}

const MpItemOtherContentEquipmentGrid = (props: Props, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [rowData, setRowData] = useState<MpResult[]>([]);
  const [isOpenUserReadModal, setOpenUserReadModal] = useState<boolean>(false);
  const [userReadModalCondition, setUserReadModalCondition] = useState<any>({});
  const [isOpenEquipmentHisModal, setOpenEquipmentHisModal] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    saveEquipment: (item: MpPlntList, equipments: CustomEquipment[], chgRsnCtn) => {
      /* if ((equipments || []).length < 1) {
        // 추가된 설비가 없습니다.
        return;
      } */
      if (
        [item?.mpPrdnPlntId, item?.mpItemId, item?.prdnPlntCd, item?.copCd].filter((o) => !o)
          .length > 0
      ) {
        // 공장 정보가 없습니다.
        return;
      }
      const addRows = (equipments || []).reduce(
        (acc, cur) => [
          ...acc,
          {
            mpPrdnPlntId: item?.mpPrdnPlntId,
            itemId: item?.mpItemId,
            plntCd: cur?.prdnPlntCd,
            copCd: cur?.copCd,
            eqpId: cur.equipmentId,
            cortCrgeUserId: cur?.cortCrgeUserId,
            chgRsnCtn: cur?.chgRsnCtn,
            horExplYn: 'Y',
          },
        ],
        [] as MpResult[]
      );

      return new Promise((resolve) => {
        saveEquipment(item, addRows, chgRsnCtn)
          .then((res) => {
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('com.label.저장되었습니다.', '저장되었습니다.')
                  : res.data && typeof res.data === 'string'
                  ? res.data
                  : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => {
            resolve(true);
          });
      });
    },
    searchMpResult: (mpItemId, copCd, prdnPlntCd, mpPrdnPlntId) => {
      searchMpResult(mpItemId, copCd, prdnPlntCd, mpPrdnPlntId);
    },
    getRowData: () => {
      return rowData;
    },
  }));

  useEffect(() => {
    searchMpResult(props.mpItemId, props.copCd, props.prdnPlntCd, props.mpPrdnPlntId);
  }, [props.mpItemId, props.copCd, props.prdnPlntCd, props.mpPrdnPlntId]);

  const searchMpResult = (mpItemId, copCd, prdnPlntCd, mpPrdnPlntId) => {
    if (mpItemId && copCd && prdnPlntCd && mpPrdnPlntId) {
      findMpResults(mpItemId, copCd, prdnPlntCd, mpPrdnPlntId).then((result) => setRowData(result));
    } else {
      setRowData([]);
    }
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'processName',
        header: String(t('mp.label.공정', '공정')),
        width: 150,
        align: 'center',
      },
      {
        binding: 'equipmentGroup',
        header: String(t('mp.label.설비군', '설비군')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'eqpId',
        header: String(t('mp.label.설비ID', '설비ID')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'equipmentName',
        header: String(t('mp.label.설비명', '설비명')),
        width: '*',
        minWidth: 250,
        align: 'left',
      },
      {
        binding: 'eqpLvNm',
        header: String(t('mp.label.설비레벨', '설비레벨')),
        width: 150,
        align: 'center',
        cellTemplate: (params) =>
          `${[params.item.eqpLv, params.item.eqpLvNm].filter((o) => !!o).join(' : ')}`,
      },
      {
        binding: 'mpItemStatCdNm',
        header: String(t('mp.label.상태', '상태')),
        width: 123,
        align: 'center',
        // [25.07.07] 4MCC번호가 있는 경우에만 상태 노출 (추가수평전개 or 재진행 대상 설비 구분하기 위함)
        cellTemplate: (params) => `${params.item.m4ChgNo && params.value ? params.value : ''}`,
      },
      {
        binding: 'cortCrgeUserNm',
        header: String(t('mp.label.개조 담당자', '개조 담당자')),
        width: 140,
        align: 'left',
        cssClass: 'WijmoFind',
        cellTemplate: (ctx) => `
          <span>${ctx.value || ''}</span>
          ${ctx.value ? '<Button />' : ''}`,
      },
      {
        binding: 'm4ChgNo',
        header: String(t('mp.label.4MCC 발행번호', '4MCC 발행번호')),
        width: 180,
        align: 'center',
        cssClass: 'WijmoPopup',
        cellTemplate: (params) => `
          <span>${params.item.m4ChgNo || ''}</span>
          ${params.item.m4ChgNo ? '<Button />' : ''} 
        `,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('pjm.label.최종수정일', '최종수정일')),
        minWidth: 115,
        align: 'center',
      },
      {
        binding: 'cortCrgeUserId',
        visible: false,
      },
    ];
  }, []);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return;

      const item = grid.rows[ht.row].dataItem;

      if (ht.panel === grid.cells) {
        const binding = grid.columns[ht.col].binding;
        // 개조 담당자
        if ('cortCrgeUserNm' === binding) {
          if (item.cortCrgeUserId) {
            setUserReadModalCondition({
              title: t('mp.label.개조 담당자', '개조 담당자'),
              initParam: item.cortCrgeUserId,
            });
            setOpenUserReadModal(true);
          }
        }
        // 4MCC 발행번호
        if ('m4ChgNo' === binding) {
          if (item.m4ChgNo) {
            open4mcc(item.m4ChgNo);
          }
        }
      }
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('mp.label.수평전개 대상 설비 List', '수평전개 대상 설비 List')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {props?.prdnPlntNm && <div className="info warning">{props?.prdnPlntNm}</div>}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="docu"
            onClick={() => {
              setOpenEquipmentHisModal(true);
            }}
            disableRipple
          >
            {t('mp.label.설비 변경 이력', '설비 변경 이력')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={250}
        isSelector={false}
        isReadOnly={true}
        allowPinning={false}
        initialized={onInitialized}
      />

      {isOpenUserReadModal && (
        <GatingContListPopUp
          open={isOpenUserReadModal}
          close={() => setOpenUserReadModal(false)}
          title={userReadModalCondition?.title}
          initParam={userReadModalCondition?.initParam}
        />
      )}

      {isOpenEquipmentHisModal && (
        <MpEquipmentHistoryPopUp
          open={isOpenEquipmentHisModal}
          close={() => setOpenEquipmentHisModal(false)}
          title={t('mp.label.설비 변경 이력 조회', '설비 변경 이력 조회')}
          condition={{
            mpItemId: props?.mpItemId || '',
            prdnPlntCd: props?.prdnPlntCd || '',
            horExplYn: 'Y',
          }}
        />
      )}
    </>
  );
};

export default React.forwardRef(MpItemOtherContentEquipmentGrid);
